/* Koleksiyon filtre / sıralama / sayfalama: <collection-facets>, <facets-drawer>, <price-range>
   Filtre değişince URL güncellenir, bölgeler Section Rendering API ile yenilenir. JS yoksa form GET ile gönderilir. */

const FACETS_DESKTOP = window.matchMedia('(min-width: 990px)');
const DENSITY_KEY = 'cpl-collection-density';

/* <facets-drawer> — mobil/tablet alttan sheet (masaüstünde kenar sütun) */
if (!customElements.get('facets-drawer')) {
  class FacetsDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer__panel');
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-facets-close]')) this.close();
      });
      this.addEventListener('keyup', (e) => e.key === 'Escape' && this.close());
      FACETS_DESKTOP.addEventListener('change', () => FACETS_DESKTOP.matches && this.close());
    }
    open(opener) {
      if (FACETS_DESKTOP.matches) return;
      this.openerId = opener?.id;
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-modal', 'true');
      this.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      opener?.setAttribute('aria-expanded', 'true');
      CPL.trapFocus(this.panel);
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.panel.removeAttribute('role');
      this.panel.removeAttribute('aria-modal');
      document.body.classList.remove('overflow-hidden');
      const opener = this.openerId && document.getElementById(this.openerId);
      opener?.setAttribute('aria-expanded', 'false');
      CPL.removeTrapFocus(opener || null);
    }
  }
  customElements.define('facets-drawer', FacetsDrawer);
}

/* <price-range> — çift tutamaklı fiyat aralığı + sayı alanları */
if (!customElements.get('price-range')) {
  class PriceRange extends HTMLElement {
    connectedCallback() {
      this.max = Number(this.dataset.max) || 0;
      this.rangeMin = this.querySelector('.facet-price__range--min');
      this.rangeMax = this.querySelector('.facet-price__range--max');
      this.numMin = this.querySelector('.facet-price__number--min');
      this.numMax = this.querySelector('.facet-price__number--max');
      this.rangeMin.addEventListener('input', () => this.fromRange('min'));
      this.rangeMax.addEventListener('input', () => this.fromRange('max'));
      this.numMin.addEventListener('input', () => this.fromNumber());
      this.numMax.addEventListener('input', () => this.fromNumber());
      this.paint();
    }
    fromRange(which) {
      let lo = Number(this.rangeMin.value);
      let hi = Number(this.rangeMax.value);
      if (lo > hi) {
        if (which === 'min') lo = hi;
        else hi = lo;
        this.rangeMin.value = lo;
        this.rangeMax.value = hi;
      }
      this.numMin.value = lo > 0 ? lo : '';
      this.numMax.value = hi < this.max ? hi : '';
      this.paint();
    }
    fromNumber() {
      this.rangeMin.value = this.numMin.value === '' ? 0 : this.numMin.value;
      this.rangeMax.value = this.numMax.value === '' ? this.max : this.numMax.value;
      this.paint();
    }
    paint() {
      const lo = (Number(this.rangeMin.value) / (this.max || 1)) * 100;
      const hi = (Number(this.rangeMax.value) / (this.max || 1)) * 100;
      this.style.setProperty('--range-min', `${lo}%`);
      this.style.setProperty('--range-max', `${hi}%`);
    }
  }
  customElements.define('price-range', PriceRange);
}

/* <collection-facets> — bölüm denetleyicisi */
if (!customElements.get('collection-facets')) {
  class CollectionFacets extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.baseUrl = this.dataset.url || window.location.pathname;
      this.formId = `FacetsForm-${this.sectionId}`;
      this.debouncedSubmit = CPL.debounce(() => this.submitForm(), 350);
      this.slowSubmit = CPL.debounce(() => this.submitForm(), 800);

      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('input', (e) => {
        if (e.target.matches('.facet-price__number')) this.slowSubmit();
      });
      this.addEventListener('submit', (e) => {
        if (e.target.id !== this.formId) return;
        e.preventDefault();
        this.submitForm();
        this.drawer()?.close();
      });
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('keyup', (e) => e.key === 'Escape' && this.closeSort());
      this.onDocClick = (e) => {
        if (this.sortOpen && !e.target.closest('[data-facets-sort], [data-sort-toggle]')) this.closeSort();
      };
      document.addEventListener('click', this.onDocClick);

      this.onPop = () => this.render(window.location.search.slice(1), { push: false });
      window.addEventListener('popstate', this.onPop);

      let density = null;
      try {
        density = localStorage.getItem(DENSITY_KEY);
      } catch (e) {
        /* depolama kapalı */
      }
      this.setDensity(density === 'dense' ? 'dense' : 'comfortable', false);
      this.syncToggles();
      this.observeInfinite();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      window.removeEventListener('popstate', this.onPop);
      this.observer?.disconnect();
    }

    form() {
      return document.getElementById(this.formId);
    }
    drawer() {
      return this.querySelector('facets-drawer');
    }

    onChange(e) {
      const el = e.target;
      if (!(el.form && el.form.id === this.formId)) return;
      if (el.matches('.facets-sort__input')) {
        this.closeSort();
        this.submitForm();
        return;
      }
      if (el.matches('.facet-price__number')) {
        this.slowSubmit();
        return;
      }
      this.debouncedSubmit();
    }

    onClick(e) {
      const t = e.target;
      const link = t.closest('a[data-facet-link]');
      if (link) {
        e.preventDefault();
        const inPagination = !!link.closest('[data-pagination]');
        this.render(new URL(link.href, window.location.origin).search.slice(1), { scroll: inPagination });
        return;
      }
      const more = t.closest('a[data-load-more]');
      if (more) {
        e.preventDefault();
        this.loadMore(more, true);
        return;
      }
      const opener = t.closest('[data-facets-open]');
      if (opener) {
        this.drawer()?.open(opener);
        return;
      }
      const sortToggle = t.closest('[data-sort-toggle]');
      if (sortToggle) {
        this.sortOpen ? this.closeSort() : this.openSort(sortToggle);
        return;
      }
      if (t.closest('[data-sort-close]')) {
        this.closeSort();
        return;
      }
      if (t.closest('[data-sidebar-toggle]')) {
        this.toggleAttribute('data-sidebar-hidden');
        this.syncToggles();
        return;
      }
      const dens = t.closest('[data-density]');
      if (dens) {
        this.setDensity(dens.dataset.density, true);
        return;
      }
      const facetMore = t.closest('[data-facet-more]');
      if (facetMore) this.toggleFacetMore(facetMore);
    }

    /* Form → URL parametreleri (boş / tam aralık fiyatı çıkar) */
    params() {
      const form = this.form();
      const params = new URLSearchParams();
      if (!form) return params;
      const fd = new FormData(form);
      const priceMax = Number(form.querySelector('price-range')?.dataset.max || 0);
      for (const [key, value] of fd.entries()) {
        if (value === '') continue;
        if (key.endsWith('price.gte') && Number(value) <= 0) continue;
        if (key.endsWith('price.lte') && priceMax && Number(value) >= priceMax) continue;
        params.append(key, value);
      }
      return params;
    }

    submitForm() {
      this.render(this.params().toString());
    }

    sectionUrl(search) {
      const sep = search ? `${search}&` : '';
      return `${this.baseUrl}?${sep}section_id=${this.sectionId}`;
    }

    showSkeleton() {
      const results = this.querySelector('[data-facets-region="results"]');
      const grid = results?.querySelector('[data-product-grid]');
      const tpl = this.querySelector('template[data-skeleton]');
      if (!results) return;
      results.setAttribute('aria-busy', 'true');
      results.classList.add('is-loading');
      if (grid && tpl) {
        const count = window.matchMedia('(min-width: 750px)').matches ? 6 : 4;
        const frag = document.createDocumentFragment();
        for (let i = 0; i < count; i++) frag.appendChild(tpl.content.cloneNode(true));
        grid.replaceChildren(frag);
      }
    }

    async render(search, { push = true, scroll = false } = {}) {
      this.abort?.abort();
      this.abort = new AbortController();
      const state = this.captureState();
      this.showSkeleton();
      if (scroll) this.scrollToResults();

      let html;
      try {
        const res = await fetch(this.sectionUrl(search), { signal: this.abort.signal });
        if (!res.ok) throw new Error(res.status);
        html = await res.text();
      } catch (e) {
        if (e.name === 'AbortError') return;
        window.location.href = `${this.baseUrl}${search ? `?${search}` : ''}`;
        return;
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      this.querySelectorAll('[data-facets-region]').forEach((region) => {
        const name = region.dataset.facetsRegion;
        const fresh = doc.querySelector(`[data-facets-region="${name}"]`);
        if (fresh) region.replaceWith(fresh);
      });
      // Yeni yanıtta olmayan bölge (ör. filtre yok → mobilbar yok) eklenmez; mevcut kalır
      this.restoreState(state);
      this.syncToggles();
      this.setDensity(this.dataset.view || 'comfortable', false);
      this.observeInfinite();

      const live = this.querySelector('[data-facets-live]');
      const countText = this.querySelector('[data-facets-count-text]');
      if (live && countText) live.textContent = countText.textContent;

      if (push) {
        const url = `${this.baseUrl}${search ? `?${search}` : ''}`;
        window.history.pushState({ facets: true }, '', url);
      }
    }

    /* Odak, açık gruplar, "daha fazla" durumları, sheet kaydırma */
    captureState() {
      return {
        focusId: document.activeElement?.id || null,
        open: Array.from(this.querySelectorAll('details[data-facet]')).map((d) => [d.id, d.open]),
        expanded: Array.from(this.querySelectorAll('.facet__list.is-expanded')).map((l) => l.id),
        scroll: this.querySelector('.facets-drawer__body')?.scrollTop || 0,
      };
    }
    restoreState(state) {
      state.open.forEach(([id, open]) => {
        const d = document.getElementById(id);
        if (d) d.open = open;
      });
      state.expanded.forEach((id) => {
        const list = document.getElementById(id);
        const btn = list?.querySelector('[data-facet-more]');
        if (btn) this.toggleFacetMore(btn, true);
      });
      const body = this.querySelector('.facets-drawer__body');
      if (body) body.scrollTop = state.scroll;
      if (state.focusId) {
        const el = document.getElementById(state.focusId);
        if (el && this.contains(el)) el.focus({ preventScroll: true });
      }
    }

    scrollToResults() {
      const top = this.getBoundingClientRect().top + window.scrollY;
      const header = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
      window.scrollTo({ top: Math.max(0, top - header - 16), behavior: 'smooth' });
    }

    /* Daha fazla göster / sonsuz kaydırma: sonraki sayfanın kartlarını ekle */
    async loadMore(link, fromClick) {
      if (this.loadingMore || !link) return;
      this.loadingMore = true;
      const region = this.querySelector('[data-pagination-region]');
      const pag = region?.querySelector('[data-pagination]');
      link.classList.add('is-loading');
      pag?.classList.add('is-loading');

      try {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('section_id', this.sectionId);
        const res = await fetch(url.toString());
        if (!res.ok) throw new Error(res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const grid = this.querySelector('[data-product-grid]');
        const items = doc.querySelectorAll('[data-product-grid] > [data-grid-item]');
        const first = items[0];
        grid?.append(...items);
        const freshRegion = doc.querySelector('[data-pagination-region]');
        if (region && freshRegion) region.replaceWith(freshRegion);
        if (fromClick && first) first.querySelector('.card-product__link')?.focus({ preventScroll: true });
      } catch (e) {
        window.location.href = link.href;
        return;
      } finally {
        this.loadingMore = false;
      }
      this.observeInfinite();
    }

    observeInfinite() {
      this.observer?.disconnect();
      if (this.dataset.pagination !== 'infinite' || !('IntersectionObserver' in window)) return;
      const pag = this.querySelector('[data-pagination="infinite"]');
      const link = pag?.querySelector('a[data-load-more]');
      if (!pag || !link) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((en) => en.isIntersecting)) this.loadMore(link, false);
        },
        { rootMargin: '400px 0px' }
      );
      this.observer.observe(pag);
    }

    /* Sıralama: masaüstünde açılır liste, mobilde alttan sheet */
    openSort(toggle) {
      const panel = this.querySelector('[data-sort-panel]');
      if (!panel) return;
      this.sortOpen = true;
      this.sortOpenerId = toggle.id;
      panel.classList.add('is-open');
      this.querySelector('.facets-sort__overlay')?.classList.add('is-open');
      this.querySelectorAll('[data-sort-toggle]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      const checked = panel.querySelector('input:checked') || panel.querySelector('input');
      if (!FACETS_DESKTOP.matches) {
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
        document.body.classList.add('overflow-hidden');
        CPL.trapFocus(panel, checked);
      } else {
        checked?.focus();
      }
    }
    closeSort() {
      if (!this.sortOpen) return;
      this.sortOpen = false;
      const panel = this.querySelector('[data-sort-panel]');
      panel?.classList.remove('is-open');
      panel?.removeAttribute('role');
      panel?.removeAttribute('aria-modal');
      this.querySelector('.facets-sort__overlay')?.classList.remove('is-open');
      this.querySelectorAll('[data-sort-toggle]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      const opener = this.sortOpenerId && document.getElementById(this.sortOpenerId);
      if (!FACETS_DESKTOP.matches) {
        document.body.classList.remove('overflow-hidden');
        CPL.removeTrapFocus(opener || null);
      } else if (opener && this.contains(document.activeElement)) {
        opener.focus();
      }
    }

    toggleFacetMore(btn, forceOpen) {
      const list = btn.closest('.facet__list');
      if (!list) return;
      const open = forceOpen === true ? true : !list.classList.contains('is-expanded');
      if (!btn.dataset.labelMore) btn.dataset.labelMore = btn.textContent.trim();
      list.classList.toggle('is-expanded', open);
      btn.setAttribute('aria-expanded', open);
      btn.textContent = open ? btn.dataset.labelLess : btn.dataset.labelMore;
    }

    setDensity(value, persist) {
      this.dataset.view = value;
      this.querySelectorAll('[data-density]').forEach((b) => b.setAttribute('aria-pressed', b.dataset.density === value));
      if (persist) {
        try {
          localStorage.setItem(DENSITY_KEY, value);
        } catch (e) {
          /* depolama kapalı */
        }
      }
    }

    syncToggles() {
      const hidden = this.hasAttribute('data-sidebar-hidden');
      this.querySelectorAll('[data-sidebar-toggle]').forEach((b) => b.setAttribute('aria-expanded', !hidden));
    }
  }
  customElements.define('collection-facets', CollectionFacets);
}
